import { ErrorMessage } from '@hookform/error-message';
import { Box, TextField, TextFieldProps } from '@mui/material';
import {
  DateRange,
  DateRangePicker,
  DateRangePickerDay,
  DateRangePickerDayProps,
} from '@mui/x-date-pickers-pro';
import classNames from 'classnames';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import {
  ControllerRenderProps,
  UseFormReturn,
  useController,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AbsenceForm } from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import { EditOverlappingAbsenceProps } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';
import { useAbsenceDateRange } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/AddAbsenceDialogContent/components/AbsenceDateRange/useAbsenceDateRange';
import { AddAbsenceFormValues } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/AddAbsenceDialogContent/useAddAbsenceDialogContent';
import { PublicHolidayDto } from '~/services/Http/client.people.generated';

import { useSxStyles } from './useStyles';

type Props = {
  formMethods: UseFormReturn<AbsenceForm>;
  publicHolidays: PublicHolidayDto[];
  onPickerMonthChange?: (month: Date) => void;
  overlappingAbsence: EditOverlappingAbsenceProps;
};

export const AbsenceDateRange = (props: Props) => {
  const {
    formMethods: {
      control,
      formState: { errors },
    },
    publicHolidays,
    onPickerMonthChange,
    overlappingAbsence,
  } = props;

  const {
    isOtherAbsenceDate,
    isOverlapAllowedOtherAbsenceDate,
    enabled: overlapAllowed,
    absenceTypes: overlapAllowedTypes,
  } = overlappingAbsence;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const absenceReason = useWatch({
    control,
    name: 'absenceReason',
    defaultValue: undefined,
  });

  const selectedReasonCanOverlap = useMemo((): boolean => {
    return (
      overlapAllowed &&
      !!absenceReason?.code &&
      overlapAllowedTypes.includes(absenceReason.code)
    );
  }, [overlapAllowed, overlapAllowedTypes, absenceReason]);

  const isOverlapAllowedDate = useCallback(
    (date: Date) => {
      const { isOverlapAllowedOtherAbsenceDate } = overlappingAbsence;

      return selectedReasonCanOverlap
        ? isOverlapAllowedOtherAbsenceDate(date, absenceReason?.code)
        : false;
    },
    [absenceReason, overlappingAbsence, selectedReasonCanOverlap],
  );

  const shouldDisableDate = useCallback(
    (date: Date) => {
      const isOverlapAllowedDate = selectedReasonCanOverlap
        ? isOverlapAllowedOtherAbsenceDate(date, absenceReason?.code)
        : false;

      return isOtherAbsenceDate(date) && !isOverlapAllowedDate;
    },
    [
      absenceReason,
      selectedReasonCanOverlap,
      isOtherAbsenceDate,
      isOverlapAllowedOtherAbsenceDate,
    ],
  );

  const { isUncountedWeekDay, isPublicHoliday, getPublicHolidayName } =
    useAbsenceDateRange({
      publicHolidays,
      absenceReasonCode: absenceReason?.code,
    });

  const {
    field: { value: startValue, onChange: onStartChange, ...startField },
  } = useController({
    name: 'startDate',
    rules: { required: true },
    control: control,
  });

  const {
    field: { value: endValue, onChange: onEndChange, ...endField },
  } = useController({
    name: 'endDate',
    rules: { required: true },
    control: control,
  });

  const handleRangeChange = (dateRange: DateRange<Date | null>) => {
    const [start, end] = dateRange;
    if (start !== startValue) {
      onStartChange(start);
    }
    if (end !== endValue) {
      onEndChange(end);
    }
  };

  const getTextFieldProps = (
    fieldProps: Omit<
      ControllerRenderProps<AddAbsenceFormValues>,
      'value' | 'onChange'
    >,
    pickerProps: TextFieldProps,
    label: string,
  ): TextFieldProps => {
    const hasFieldError = errors.hasOwnProperty(fieldProps.name);

    return {
      ...fieldProps,
      ...pickerProps,
      label,
      required: true,
      fullWidth: true,
      margin: 'normal',
      error: hasFieldError,
      helperText: hasFieldError && (
        <ErrorMessage errors={errors} name={fieldProps.name} />
      ),
    };
  };

  const renderWeekPickerDay = (
    date: Date,
    dateRangePickerDayProps: DateRangePickerDayProps<Date>,
  ) => {
    const { outsideCurrentMonth } = dateRangePickerDayProps;

    const isPH = isPublicHoliday(date);
    const isUncountedDate = isUncountedWeekDay(date) || isPH;
    const isOtherAbsence = isOtherAbsenceDate(date);
    const isOverlapAllowed = isOverlapAllowedDate(date);

    const resolveTitle = (date: Date) => {
      const absenceTitle = isOtherAbsence ? t('absence') : undefined;

      return getPublicHolidayName(date) ?? absenceTitle;
    };

    return (
      <DateRangePickerDay
        {...dateRangePickerDayProps}
        title={resolveTitle(date)}
        className={
          (!outsideCurrentMonth &&
            classNames(
              isUncountedDate && 'uncounted',
              isPH && 'publicHoliday',
              isOtherAbsence && 'absence',
              isOverlapAllowed && 'absenceOverlap',
            )) ||
          undefined
        }
      />
    );
  };

  return (
    <DateRangePicker
      value={[startValue, endValue]}
      onChange={handleRangeChange}
      onMonthChange={onPickerMonthChange}
      shouldDisableDate={shouldDisableDate}
      PopperProps={{
        sx: sxStyles.pickerPopperRoot,
      }}
      renderDay={renderWeekPickerDay}
      renderInput={(startProps, endProps) => (
        <>
          <TextField
            {...getTextFieldProps(startField, startProps, t('startDate'))}
          />
          <Box sx={sxStyles.rangeDivider}>-</Box>
          <TextField {...getTextFieldProps(endField, endProps, t('endDate'))} />
        </>
      )}
    />
  );
};
